// src/components/HeroCarousel/HeroCarousel.js
import React, { useState, useEffect } from 'react';
import { ChevronRight, BarChart2 } from 'lucide-react';
import FeedbackTimeline from '../../routes/SplashView/FeedbackVisual'; // Adjust path as needed
import './HeroCarousel.css';

const HeroCarousel = ({ scoreGifs }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Add pause state for manual interactions

  const slides = [
    {
      id: 0,
      type: 'timeline',
      content: null // Will render FeedbackTimeline
    },
    {
    id: 1,
    type: 'message',
    content: {
        title: "While Others Guess",
        subtitle: "You'll Know",
        description: "",
        highlight: "That's the difference."
    }
    }
  ];

  // Auto-rotate carousel every 15 seconds
  useEffect(() => {
    if (isPaused) return; // Don't auto-rotate if paused

    const interval = setInterval(() => {
      setCurrentSlide(prevSlide => (prevSlide + 1) % slides.length);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [isPaused, slides.length]);

  // Resume auto-rotation after manual interaction
  useEffect(() => {
    if (isPaused) {
      const resumeTimer = setTimeout(() => {
        setIsPaused(false);
      }, 10000); // Resume after 10 seconds of no interaction

      return () => clearTimeout(resumeTimer);
    }
  }, [isPaused]);

  const handleSlideChange = (newSlide) => {
    if (newSlide === currentSlide || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(newSlide);
    setIsPaused(true); // Pause auto-rotation when user manually interacts
    
    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 500);
  };

  return (
    <div className="hero-carousel-container">
      {/* Carousel Container with pause on hover */}
      <div 
        className="hero-carousel-wrapper"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        
        {/* Slides */}
        <div 
          className="hero-carousel-slides"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {/* Slide 1: FeedbackTimeline */}
          <div className="hero-carousel-slide">
            <div className="hero-carousel-timeline">
              <FeedbackTimeline 
                scoreGifs={scoreGifs}
                showNumbers={false}
                className="w-full max-w-3xl" 
              />
            </div>
          </div>

          {/* Slide 2: Message */}
          <div className="hero-carousel-slide">
            <div className="hero-carousel-message">
              <div className="hero-carousel-icon">
                <div className="hero-carousel-icon-wrapper">
                  <BarChart2 className="hero-carousel-icon-svg" />
                </div>
              </div>
              
              <h3 className="hero-carousel-title">
                {slides[1].content.title}
              </h3>
              
              <p className="hero-carousel-subtitle">
                {slides[1].content.subtitle}
              </p>
              
              <p className="hero-carousel-description">
                {slides[1].content.description}
              </p>
              
              <p className="hero-carousel-highlight">
                {slides[1].content.highlight}
              </p>
              
              <div className="hero-carousel-tagline">
                <p className="hero-carousel-tagline-text">
                  fdbk gives you that information
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="hero-carousel-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => handleSlideChange(index)}
              className={`hero-carousel-dot ${
                currentSlide === index ? 'active' : ''
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => handleSlideChange((currentSlide - 1 + slides.length) % slides.length)}
          className="hero-carousel-arrow hero-carousel-arrow-left"
          aria-label="Previous slide"
        >
          <ChevronRight className="hero-carousel-arrow-icon rotate-180" />
        </button>
        
        <button
          onClick={() => handleSlideChange((currentSlide + 1) % slides.length)}
          className="hero-carousel-arrow hero-carousel-arrow-right"
          aria-label="Next slide"
        >
          <ChevronRight className="hero-carousel-arrow-icon" />
        </button>

        {/* Progress Bar */}
        <div className="hero-carousel-progress-bar">
          <div 
            className="hero-carousel-progress-fill"
            style={{ 
              width: `${((currentSlide + 1) / slides.length) * 100}%` 
            }}
          />
        </div>
      </div>

      {/* Carousel Caption */}
      <div className="hero-carousel-caption">
        <div className="hero-carousel-caption-content">
          {slides.map((_, index) => (
            <span
              key={index}
              className={`hero-carousel-caption-text ${
                currentSlide === index ? 'active' : ''
              }`}
            >
              {index === 0 ? 'Real time Feedback' : 'The Power of Information'}
              {index < slides.length - 1 && currentSlide !== index && (
                <span className="hero-carousel-separator">•</span>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroCarousel;